import { useState } from "react";

const growthTips = [
  {
    icon: "ri-global-line",
    title: "24/7 Online Visibility",
    desc: "Your website works while you sleep. Potential clients can find you, learn about your services, and reach out at any hour — even when you're off the clock.",
  },
  {
    icon: "ri-calendar-check-line",
    title: "Automated Appointment Booking",
    desc: "A booking system on your site eliminates back-and-forth calls. Clients self-schedule, reducing friction and converting interest into confirmed appointments instantly.",
  },
  {
    icon: "ri-map-pin-2-line",
    title: "Local SEO Dominance",
    desc: "When someone searches for your service in your area, a well-built website puts you at the top of Google — capturing clients who are actively looking to buy right now.",
  },
  {
    icon: "ri-shield-star-line",
    title: "Instant Credibility & Trust",
    desc: "Over 75% of consumers judge a business's credibility by its website. A professional site signals you're established, trustworthy, and worth the investment.",
  },
  {
    icon: "ri-share-forward-line",
    title: "Word-of-Mouth Amplified",
    desc: "When happy clients refer you, the first thing new prospects do is Google you. A strong website turns referrals into closed deals instead of lost opportunities.",
  },
  {
    icon: "ri-bar-chart-2-line",
    title: "Track & Grow Smarter",
    desc: "Analytics show you exactly where clients come from, what they look at, and what makes them reach out — so you can double down on what works.",
  },
];

export default function ROICalculator() {
  const [avgSale, setAvgSale] = useState("");
  const [currentClients, setCurrentClients] = useState("");
  const [showResult, setShowResult] = useState(false);

  const avgSaleNum = parseFloat(avgSale.replace(/[^0-9.]/g, "")) || 0;
  const currentClientsNum = parseFloat(currentClients.replace(/[^0-9.]/g, "")) || 0;

  // Conservative estimate: website adds 30% more clients per month
  const additionalClients = Math.round(currentClientsNum * 0.3);
  const monthlyGain = additionalClients * avgSaleNum;
  const annualGain = monthlyGain * 12;

  const handleCalculate = () => {
    if (avgSaleNum > 0 && currentClientsNum > 0) {
      setShowResult(true);
    }
  };

  const formatCurrency = (val: number) =>
    val.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

  return (
    <section className="py-28 bg-[#080b18] relative overflow-hidden">
      {/* Background accents */}
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] rounded-full bg-[#d4af37]/5 blur-[120px] pointer-events-none"></div>
      <div className="absolute bottom-0 right-0 w-[300px] h-[300px] rounded-full bg-[#1a2a5e]/15 blur-[100px] pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-[#d4af37] text-xs font-semibold tracking-widest uppercase mb-4 block">Business Growth</span>
          <h2 className="text-4xl md:text-5xl font-black text-white leading-tight mb-4">
            How Much More Could<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d4af37] to-[#f0d060]">Your Business Be Making?</span>
          </h2>
          <p className="text-white/50 text-base max-w-xl mx-auto leading-relaxed">
            Most businesses without a website are leaving serious money on the table. See exactly how much a professional website could add to your bottom line.
          </p>
        </div>

        {/* Growth Tips Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-20">
          {growthTips.map((tip) => (
            <div
              key={tip.title}
              className="group p-6 rounded-2xl border border-white/5 bg-[#0c0f1c] hover:border-[#d4af37]/20 transition-all duration-300"
            >
              <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#d4af37]/10 border border-[#d4af37]/20 mb-4 group-hover:bg-[#d4af37]/20 transition-colors duration-300">
                <i className={`${tip.icon} text-[#d4af37] text-lg`}></i>
              </div>
              <h3 className="text-white font-bold text-sm mb-2">{tip.title}</h3>
              <p className="text-white/45 text-sm leading-relaxed">{tip.desc}</p>
            </div>
          ))}
        </div>

        {/* ROI Calculator */}
        <div className="max-w-3xl mx-auto">
          <div className="bg-[#0c0f1c] border border-white/5 rounded-2xl p-8 md:p-10">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#d4af37]/10 border border-[#d4af37]/20">
                <i className="ri-calculator-line text-[#d4af37] text-lg"></i>
              </div>
              <h3 className="text-white font-black text-xl">Revenue Growth Estimator</h3>
            </div>
            <p className="text-white/40 text-sm mb-8 ml-[52px]">
              Answer two quick questions and see your potential upside.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
              <div>
                <label className="text-white/50 text-xs font-medium mb-2 block tracking-wide">
                  What&apos;s your average sale or job value?
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#d4af37] text-sm font-bold">$</span>
                  <input
                    type="number"
                    min="0"
                    value={avgSale}
                    onChange={(e) => { setAvgSale(e.target.value); setShowResult(false); }}
                    placeholder="e.g. 500"
                    className="w-full bg-[#080b18] border border-white/10 rounded-xl pl-8 pr-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#d4af37]/40 transition-colors duration-200"
                  />
                </div>
                <p className="text-white/25 text-xs mt-1.5">Per client, project, or appointment</p>
              </div>

              <div>
                <label className="text-white/50 text-xs font-medium mb-2 block tracking-wide">
                  How many new clients do you get per month?
                </label>
                <input
                  type="number"
                  min="0"
                  value={currentClients}
                  onChange={(e) => { setCurrentClients(e.target.value); setShowResult(false); }}
                  placeholder="e.g. 10"
                  className="w-full bg-[#080b18] border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#d4af37]/40 transition-colors duration-200"
                />
                <p className="text-white/25 text-xs mt-1.5">Your current monthly average</p>
              </div>
            </div>

            <button
              onClick={handleCalculate}
              disabled={!avgSale || !currentClients}
              className="w-full py-4 rounded-xl bg-[#d4af37] text-[#050810] font-bold text-sm tracking-wide hover:bg-[#e8c84a] transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              <i className="ri-line-chart-line"></i>
              Show My Growth Potential
            </button>

            {/* Result */}
            {showResult && (
              <div className="mt-8 p-6 rounded-xl bg-[#d4af37]/8 border border-[#d4af37]/20">
                <p className="text-[#d4af37] text-xs font-semibold tracking-widest uppercase mb-5">Your Estimated Growth Potential</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                  <div className="text-center p-4 rounded-xl bg-[#080b18] border border-white/5">
                    <div className="text-2xl font-black text-[#d4af37] mb-1">+{additionalClients}</div>
                    <div className="text-white/40 text-xs">Extra clients/month</div>
                  </div>
                  <div className="text-center p-4 rounded-xl bg-[#080b18] border border-white/5">
                    <div className="text-2xl font-black text-[#d4af37] mb-1">{formatCurrency(monthlyGain)}</div>
                    <div className="text-white/40 text-xs">Additional monthly revenue</div>
                  </div>
                  <div className="text-center p-4 rounded-xl bg-[#080b18] border border-white/5">
                    <div className="text-2xl font-black text-[#d4af37] mb-1">{formatCurrency(annualGain)}</div>
                    <div className="text-white/40 text-xs">Additional annual revenue</div>
                  </div>
                </div>
                <p className="text-white/50 text-xs leading-relaxed mb-4">
                  Based on a conservative <strong className="text-white/70">30% increase</strong> in new clients — which is typical for businesses that go from no web presence to a professionally built, SEO-optimized website. Many of our clients see even higher growth in the first 6 months.
                </p>
                <div className="flex items-center gap-3 p-4 rounded-xl bg-[#d4af37]/10 border border-[#d4af37]/20">
                  <i className="ri-lightbulb-flash-line text-[#d4af37] text-xl flex-shrink-0"></i>
                  <p className="text-white/70 text-xs leading-relaxed">
                    <strong className="text-white">Think about it this way:</strong> If our services cost a fraction of {formatCurrency(annualGain)}, your website pays for itself many times over — every single year.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
